/**
 * Turn Promang objects into label records: flat { key: string } maps whose
 * keys match MODULE_FIELDS in labelModel.js. Each record also carries `_id`
 * and `_title` for the dialog header.
 */
import { fetchProducts } from "../../api/product";

const PRODUCT_FIELDS =
  "id,item_number,title,short_description,sales_price,brand,categories";

function origin() {
  return typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://promang.no";
}

/** Plain single-line text from (possibly HTML) content. */
export function plainText(value) {
  if (value == null) return "";
  return String(value)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h\d)>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function today() {
  return new Date().toLocaleDateString("nb-NO");
}

const priceFormat = new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK" });

export function formatPrice(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? priceFormat.format(n) : "";
}

function nameOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.title || value.name || value.displayTitle || "";
}

/**
 * Product → record. Accepts a product, or a warehouse-stock / picking row
 * (`{ product, storage }`). `location` overrides the storage name.
 */
export function productRecord(source, { location } = {}) {
  const product = source?.product && typeof source.product === "object" ? source.product : source || {};
  const id = product.id ?? source?.product_id;
  // Picking lines carry one `storage`; warehouse-stock rows list every `storages` entry.
  const storages = Array.isArray(source?.storages)
    ? source.storages.map((s) => s?.title || s?.storage_number || "").filter(Boolean).join(", ")
    : "";
  const storage = location ?? (source?.storage?.title || source?.storage_title || storages);
  const categories = Array.isArray(product.categories)
    ? product.categories.map((c) => c?.displayTitle || c?.title || "").filter(Boolean).join(", ")
    : "";
  return {
    _id: id,
    _title: plainText(product.title) || product.item_number || (id ? `#${id}` : ""),
    nr: product.item_number || "",
    navn: plainText(product.title),
    pris: formatPrice(product.sales_price),
    beskrivelse: plainText(product.short_description),
    merke: nameOf(product.brand),
    kategori: categories,
    lokasjon: storage || "",
    url: id ? `${origin()}/dashboard/products/${id}` : "",
    dato: today(),
  };
}

export function projectRecord(project = {}) {
  return {
    _id: project.id,
    _title: project.name || project.project_number || "",
    nr: project.project_number || "",
    navn: plainText(project.name),
    kunde: nameOf(project.customer),
    beskrivelse: plainText(project.description),
    url: project.id ? `${origin()}/dashboard/projects/${project.id}` : "",
    dato: today(),
  };
}

export function reportRecord(report = {}) {
  return {
    _id: report.id,
    _title: report.report_number || "",
    nr: report.report_number || "",
    navn: nameOf(report.report_type),
    kunde: nameOf(report.customer),
    adresse: nameOf(report.delivery_address),
    url: report.id ? `${origin()}/dashboard/service/reports/${report.id}` : "",
    dato: today(),
  };
}

export function toolRecord(tool = {}) {
  return {
    _id: tool.id,
    _title: tool.title || tool.tool_number || "",
    nr: tool.tool_number || "",
    navn: plainText(tool.title),
    modell: tool.model || "",
    serienr: tool.serial_number || "",
    merke: nameOf(tool.brand) || nameOf(tool.category),
    url: tool.id ? `${origin()}/dashboard/tools/${tool.id}` : "",
    dato: today(),
  };
}

/** Picking-list document label ({ title, subtitle, description, description_two, qr_code }). */
export function documentRecord(label = {}, id) {
  return {
    _id: id,
    _title: label.title || "",
    nr: plainText(label.title),
    navn: plainText(label.subtitle),
    beskrivelse: plainText(label.description),
    info: plainText(label.description_two),
    url: label.qr_code || "",
    dato: today(),
  };
}

/**
 * Load full product data for label records (list rows only carry a few fields).
 * Keeps the caller's order and any per-row location; missing products fall
 * back to the data the caller already had.
 */
export async function loadProductRecords(sources) {
  const rows = (sources || []).filter(Boolean);
  const idOf = (row) => row?.product?.id ?? row?.product_id ?? row?.id;
  const ids = [...new Set(rows.map(idOf).filter((id) => id != null))];
  if (!ids.length) return [];
  const response = await fetchProducts({
    ids: ids.join(","),
    fields: PRODUCT_FIELDS,
    show_inactive: true,
    page: 1,
    per_page: ids.length,
  });
  const byId = new Map((response?.data || []).map((p) => [String(p.id), p]));
  return rows
    .filter((row) => idOf(row) != null)
    .map((row) => {
      const full = byId.get(String(idOf(row)));
      const base = row.product && typeof row.product === "object" ? row.product : row;
      return productRecord(
        { ...row, product: { ...base, ...(full || {}), id: idOf(row) } },
        {}
      );
    });
}
